import { useState, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { formatAUD, generateId } from '@/lib/format';
import { Asset, DEFAULT_ROOMS } from '@/lib/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Plus, Camera, Package, Trash2, ChevronRight, Home } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

function AddAssetForm({ onSave, onCancel }: { onSave: (asset: Asset) => void; onCancel: () => void }) {
  const { toast } = useToast();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [purchaseDate, setPurchaseDate] = useState(new Date().toISOString().split('T')[0]);
  const [purchasePrice, setPurchasePrice] = useState('');
  const [currentValue, setCurrentValue] = useState('');
  const [room, setRoom] = useState('');
  const [insurerName, setInsurerName] = useState('');
  const [policyNumber, setPolicyNumber] = useState('');
  const [photo, setPhoto] = useState<string | undefined>();
  const fileRef = useRef<HTMLInputElement>(null);

  const compressImage = (file: File, maxWidth = 800, quality = 0.7): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ratio = Math.min(maxWidth / img.width, maxWidth / img.height, 1);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error('Canvas not supported')); return; }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        // Check compressed size (base64 is ~4/3 of original)
        if (dataUrl.length > 500_000) {
          // Try lower quality
          const lowQ = canvas.toDataURL('image/jpeg', 0.4);
          resolve(lowQ);
        } else {
          resolve(dataUrl);
        }
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  };

  const handlePhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Photo must be under 5MB', variant: 'destructive' });
      return;
    }
    try {
      const compressed = await compressImage(file);
      setPhoto(compressed);
    } catch {
      toast({ title: 'Error', description: 'Failed to process photo', variant: 'destructive' });
    }
  };

  const handleSubmit = () => {
    if (!name || !room || !purchasePrice) return;
    const price = parseFloat(purchasePrice);
    if (isNaN(price) || !isFinite(price) || price < 0) return;
    const val = currentValue ? parseFloat(currentValue) : price;
    if (isNaN(val) || !isFinite(val) || val < 0) return;
    onSave({
      id: generateId(),
      name,
      description,
      purchaseDate,
      purchasePrice: price,
      currentValue: val,
      room,
      photo,
      insurerName: insurerName || undefined,
      policyNumber: policyNumber || undefined,
      createdAt: new Date().toISOString(),
    });
  };

  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
      {/* Photo */}
      <div className="flex flex-col items-center gap-2">
        {photo ? (
          <img src={photo} alt="Asset" className="h-32 w-32 rounded-xl object-cover border border-border" />
        ) : (
          <div
            className="flex h-32 w-32 cursor-pointer items-center justify-center rounded-xl border-2 border-dashed border-border bg-muted/50"
            onClick={() => fileRef.current?.click()}
          >
            <Camera className="h-8 w-8 text-muted-foreground" />
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhoto} />
        <Button variant="ghost" size="sm" onClick={() => fileRef.current?.click()}>
          {photo ? 'Change Photo' : 'Add Photo'}
        </Button>
      </div>

      <div className="space-y-1.5">
        <Label>Item Name *</Label>
        <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Samsung TV 65&quot;" />
      </div>

      <div className="space-y-1.5">
        <Label>Room / Location *</Label>
        <Select value={room} onValueChange={setRoom}>
          <SelectTrigger><SelectValue placeholder="Select room" /></SelectTrigger>
          <SelectContent>
            {DEFAULT_ROOMS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label>Description</Label>
        <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Model, serial number, notes..." rows={2} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Purchase Price *</Label>
          <Input type="number" value={purchasePrice} onChange={e => setPurchasePrice(e.target.value)} placeholder="0.00" min="0" step="0.01" />
        </div>
        <div className="space-y-1.5">
          <Label>Current Value</Label>
          <Input type="number" value={currentValue} onChange={e => setCurrentValue(e.target.value)} placeholder="Same as purchase" min="0" step="0.01" />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Purchase Date</Label>
        <Input type="date" value={purchaseDate} onChange={e => setPurchaseDate(e.target.value)} />
      </div>

      {/* Insurance details */}
      <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-3">
        <p className="text-xs font-medium text-muted-foreground">Insurance Details (optional)</p>
        <div className="space-y-1.5">
          <Label>Insurer Name</Label>
          <Input value={insurerName} onChange={e => setInsurerName(e.target.value)} placeholder="e.g. NRMA, Allianz" />
        </div>
        <div className="space-y-1.5">
          <Label>Policy Number</Label>
          <Input value={policyNumber} onChange={e => setPolicyNumber(e.target.value)} placeholder="Policy #" />
        </div>
      </div>

      <div className="flex gap-2 pt-2">
        <Button variant="outline" className="flex-1" onClick={onCancel}>Cancel</Button>
        <Button className="flex-1" onClick={handleSubmit} disabled={!name || !room || !purchasePrice}>Save Item</Button>
      </div>
    </div>
  );
}

function AssetDetail({ asset, onDelete, onUpdateValue }: {
  asset: Asset;
  onDelete: () => void;
  onUpdateValue: (val: number) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [newValue, setNewValue] = useState(asset.currentValue.toString());

  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
      {asset.photo && (
        <img src={asset.photo} alt={asset.name} className="w-full rounded-xl object-cover max-h-48" />
      )}

      <div>
        <h3 className="font-display text-lg font-bold">{asset.name}</h3>
        <Badge variant="secondary">{asset.room}</Badge>
      </div>

      {asset.description && <p className="text-sm text-muted-foreground">{asset.description}</p>}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-xs text-muted-foreground">Purchase Price</p>
          <p className="font-display font-semibold">{formatAUD(asset.purchasePrice)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Current Value</p>
          {editing ? (
            <div className="flex gap-1">
              <Input type="number" value={newValue} onChange={e => setNewValue(e.target.value)} className="h-8 text-sm" />
              <Button size="sm" className="h-8" onClick={() => { const v = parseFloat(newValue); if (!isNaN(v) && isFinite(v) && v >= 0) { onUpdateValue(v); setEditing(false); } }}>Save</Button>
            </div>
          ) : (
            <p className="cursor-pointer font-display font-semibold text-primary" onClick={() => setEditing(true)}>
              {formatAUD(asset.currentValue)} ✎
            </p>
          )}
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Purchase Date</p>
          <p className="text-sm">{new Date(asset.purchaseDate).toLocaleDateString('en-AU')}</p>
        </div>
      </div>

      {(asset.insurerName || asset.policyNumber) && (
        <div className="rounded-lg border border-border bg-muted/30 p-3">
          <p className="mb-1 text-xs font-medium text-muted-foreground">Insurance</p>
          {asset.insurerName && <p className="text-sm">{asset.insurerName}</p>}
          {asset.policyNumber && <p className="text-xs text-muted-foreground">Policy: {asset.policyNumber}</p>}
        </div>
      )}

      <Button variant="destructive" className="w-full gap-2" onClick={onDelete}>
        <Trash2 className="h-4 w-4" /> Remove Item
      </Button>
    </div>
  );
}

export default function AssetsPage() {
  const { data, updateData } = useAuth();
  const { toast } = useToast();
  const [addOpen, setAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [roomFilter, setRoomFilter] = useState<string>('all');

  const assets = data.assets;
  const filtered = roomFilter === 'all' ? assets : assets.filter(a => a.room === roomFilter);
  const rooms = [...new Set(assets.map(a => a.room))];

  const totalValue = assets.reduce((s, a) => s + a.currentValue, 0);
  const roomTotals = rooms.map(r => ({
    room: r,
    count: assets.filter(a => a.room === r).length,
    value: assets.filter(a => a.room === r).reduce((s, a) => s + a.currentValue, 0),
  }));

  const handleSave = async (asset: Asset) => {
    await updateData(prev => ({ ...prev, assets: [...prev.assets, asset] }));
    setAddOpen(false);
    toast({ title: 'Asset added', description: `${asset.name} saved to ${asset.room}` });
  };

  const handleDelete = async (id: string) => {
    await updateData(prev => ({ ...prev, assets: prev.assets.filter(a => a.id !== id) }));
    setSelectedId(null);
    toast({ title: 'Asset removed' });
  };

  const handleUpdateValue = async (id: string, val: number) => {
    await updateData(prev => ({
      ...prev,
      assets: prev.assets.map(a => a.id === id ? { ...a, currentValue: val } : a),
    }));
  };

  const selected = assets.find(a => a.id === selectedId);

  return (
    <div className="px-4 pt-6 pb-4">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold">Asset Database</h1>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="gap-1"><Plus className="h-4 w-4" /> Add Item</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Add Asset</DialogTitle></DialogHeader>
            <AddAssetForm onSave={handleSave} onCancel={() => setAddOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      {/* Total Value Summary */}
      <Card className="mb-4 bg-primary/5 border-primary/20">
        <CardContent className="flex items-center justify-between p-4">
          <div>
            <p className="text-xs text-muted-foreground">Total Asset Value</p>
            <p className="font-display text-xl font-bold">{formatAUD(totalValue)}</p>
          </div>
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
            <Home className="h-5 w-5 text-primary" />
          </div>
        </CardContent>
      </Card>

      {/* Room breakdown */}
      {roomTotals.length > 0 && (
        <div className="mb-4 flex gap-2 overflow-x-auto pb-1">
          <Badge
            variant={roomFilter === 'all' ? 'default' : 'outline'}
            className="cursor-pointer shrink-0"
            onClick={() => setRoomFilter('all')}
          >
            All ({assets.length})
          </Badge>
          {roomTotals.map(r => (
            <Badge
              key={r.room}
              variant={roomFilter === r.room ? 'default' : 'outline'}
              className="cursor-pointer shrink-0"
              onClick={() => setRoomFilter(r.room)}
            >
              {r.room} ({r.count})
            </Badge>
          ))}
        </div>
      )}

      {/* Room value cards */}
      {roomFilter === 'all' && roomTotals.length > 1 && (
        <div className="mb-4 grid grid-cols-2 gap-2">
          {roomTotals.map(r => (
            <Card key={r.room} className="cursor-pointer" onClick={() => setRoomFilter(r.room)}>
              <CardContent className="p-3">
                <p className="text-xs text-muted-foreground">{r.room}</p>
                <p className="font-display text-sm font-bold">{formatAUD(r.value)}</p>
                <p className="text-xs text-muted-foreground">{r.count} item{r.count !== 1 ? 's' : ''}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Asset List */}
      {filtered.length === 0 ? (
        <div className="mt-12 text-center">
          <Package className="mx-auto mb-2 h-10 w-10 text-muted-foreground/50" />
          <p className="text-muted-foreground">No assets yet</p>
          <p className="text-sm text-muted-foreground">Tap "Add Item" to track your first item</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(asset => (
            <Card key={asset.id} className="cursor-pointer transition-colors hover:bg-muted/50" onClick={() => setSelectedId(asset.id)}>
              <CardContent className="flex items-center gap-3 p-3">
                {asset.photo ? (
                  <img src={asset.photo} alt={asset.name} className="h-12 w-12 rounded-lg object-cover" />
                ) : (
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-muted">
                    <Package className="h-5 w-5 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="truncate font-medium">{asset.name}</p>
                  <p className="text-xs text-muted-foreground">{asset.room}</p>
                </div>
                <div className="text-right">
                  <p className="font-display text-sm font-semibold">{formatAUD(asset.currentValue)}</p>
                  {asset.insurerName && <p className="text-xs text-muted-foreground">Insured</p>}
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={open => !open && setSelectedId(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Asset Details</DialogTitle></DialogHeader>
          {selected && (
            <AssetDetail
              asset={selected}
              onDelete={() => handleDelete(selected.id)}
              onUpdateValue={val => handleUpdateValue(selected.id, val)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
